/**
 * The rich-text control — **typed nodes, never a markup string** (ADR-0013 §4).
 *
 * There is no contenteditable here and that is the decision, not a shortcut. A
 * WYSIWYG surface produces HTML, HTML has to be sanitised, and a sanitiser is
 * a thing that is wrong once. The site shares an origin with the client portal
 * (ADR-0008), so a script that reached a public page would run beside a
 * client's session — which is why `RichText` is `{nodes: [{kind, spans}]}` and
 * why the renderer under `site/` has no `dangerouslySetInnerHTML` anywhere in
 * it. **There is nothing to sanitise because there is nowhere for markup to
 * be.**
 *
 * What a firm gets instead is a list of blocks, each with a kind — paragraph,
 * two heading levels, a quote, a bulleted or numbered list. It is less than
 * Word and it is the whole of what ADR-0013 permits.
 *
 * Emphasis and links live on spans and are not editable here yet: the control
 * writes one span per node. That is a real limit and it is stated rather than
 * hidden — a firm can see its bold text if the AI or a future control put it
 * there, and editing the text keeps it. Marked below.
 */

import { Button, Select, Textarea } from '@fiveox/ui'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'

import { EMPTY_RICH_TEXT, type RichNode, type RichNodeKind, type RichText } from '../../../lib/api/site'

const KIND_LABELS: Record<RichNodeKind, string> = {
  p: 'Paragraph',
  h2: 'Heading',
  h3: 'Sub-heading',
  quote: 'Quote',
  ul: 'Bulleted list',
  ol: 'Numbered list',
}

const LIST_KINDS: RichNodeKind[] = ['ul', 'ol']

/** The text of a node, whichever shape it is in. */
export function nodeText(node: RichNode): string {
  if (LIST_KINDS.includes(node.kind)) {
    return (node.items ?? []).map((item) => item.spans.map((s) => s.text).join('')).join('\n')
  }
  return (node.spans ?? []).map((s) => s.text).join('')
}

/**
 * Put text back into a node of a given kind, **keeping the first span's marks**.
 *
 * A firm that fixes a typo in a bold sentence should still have a bold
 * sentence. Without this the control would silently strip emphasis it cannot
 * yet edit, which is the quiet kind of data loss.
 */
export function withText(node: RichNode, kind: RichNodeKind, text: string): RichNode {
  if (LIST_KINDS.includes(kind)) {
    return {
      kind,
      items: text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => ({ spans: [{ text: line }] })),
    }
  }
  const marks = node.spans?.[0]
  return { kind, spans: [{ ...(marks ?? {}), text }] }
}

export function RichTextField({
  value,
  onChange,
}: {
  value: RichText | null | undefined
  onChange: (next: RichText) => void
}) {
  const nodes = value?.nodes ?? EMPTY_RICH_TEXT.nodes
  const replace = (next: RichNode[]) => onChange({ nodes: next })

  const update = (index: number, node: RichNode) =>
    replace(nodes.map((n, i) => (i === index ? node : n)))

  const move = (index: number, by: number) => {
    const to = index + by
    if (to < 0 || to >= nodes.length) return
    const next = [...nodes]
    const [moved] = next.splice(index, 1)
    if (moved) next.splice(to, 0, moved)
    replace(next)
  }

  return (
    <div className="space-y-2">
      {nodes.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Nothing here yet. Add a paragraph to start.
        </p>
      )}

      {nodes.map((node, index) => {
        const isList = LIST_KINDS.includes(node.kind)
        return (
          <div key={index} className="rounded-lg border border-border bg-surface-raised p-2.5">
            <div className="mb-1.5 flex items-center gap-2">
              <div className="w-44 shrink-0">
                <Select
                  value={node.kind}
                  onChange={(e) =>
                    update(index, withText(node, e.target.value as RichNodeKind, nodeText(node)))
                  }
                  aria-label="Kind"
                >
                  {(Object.keys(KIND_LABELS) as RichNodeKind[]).map((kind) => (
                    <option key={kind} value={kind}>
                      {KIND_LABELS[kind]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move up"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  <ArrowUp size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Move down"
                  disabled={index === nodes.length - 1}
                  onClick={() => move(index, 1)}
                >
                  <ArrowDown size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Remove"
                  onClick={() => replace(nodes.filter((_, i) => i !== index))}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <Textarea
              value={nodeText(node)}
              rows={isList ? 4 : 3}
              onChange={(e) => update(index, withText(node, node.kind, e.target.value))}
              placeholder={isList ? 'One item per line' : undefined}
            />
            {isList && (
              <p className="mt-1 text-xs text-muted-foreground">One item per line.</p>
            )}
          </div>
        )
      })}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => replace([...nodes, { kind: 'p', spans: [{ text: '' }] }])}
      >
        <Plus size={14} /> Add a paragraph
      </Button>
    </div>
  )
}
